import React from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Link } from 'react-router-dom';
import { ExternalLink } from 'lucide-react';

const SponsorshipPage = () => {
  return (
    <div className="min-h-screen bg-white">
      <Header />
      <main>
        {/* Hero Section */}
        <section className="bg-[#1a1a1a] py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">Community Sponsorship</h1>
            <p className="text-gray-300 max-w-2xl mx-auto">
              Proud to support our local community and contribute to the vitality of regional Victoria.
            </p>
          </div>
        </section>

        {/* Our Commitment */}
        <section className="py-16">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Our Commitment to the Community</h2>
            <div className="space-y-6 text-gray-700 leading-relaxed">
              <p>
                At <strong>Wycheproof Grain Handling Pty Ltd</strong>, we believe that a successful business grows best within a thriving community. The people, families, and farms of our region are at the heart of everything we do — and giving back to the community that has supported us for so many years is something we take great pride in.
              </p>
              <p>
                We understand that strong community connections are essential to the long-term success of both local agriculture and regional life. Whether it's sponsoring local events, supporting sporting clubs, or partnering with agricultural organisations, we see our role as more than just a service provider — we're an active contributor to the strength and resilience of our community.
              </p>
              <p>
                Through our ongoing involvement, we aim to help our region continue to grow — not just economically, but socially and sustainably. At Wycheproof Grain Handling, community isn't just where we work — it's who we are.
              </p>
            </div>
          </div>
        </section>

        {/* Areas of Support */}
        <section className="py-16 bg-[#faf8f5]">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-10 text-center">Areas of Support</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {/* Community Events */}
              <div className="bg-white rounded-lg p-6">
                <h3 className="font-bold text-gray-900 mb-3">Community Events</h3>
                <p className="text-gray-600 text-sm mb-4">
                  We proudly support signature events that bring our community together and celebrate our regional identity.
                </p>
                <ul className="space-y-2">
                  <li className="flex items-center gap-2 text-gray-700">
                    <span className="w-1.5 h-1.5 bg-yellow-500 rounded-full" />
                    Wycheproof Races
                  </li>
                </ul>
              </div>

              {/* Local Organizations */}
              <div className="bg-white rounded-lg p-6">
                <h3 className="font-bold text-gray-900 mb-3">Local Organizations</h3>
                <p className="text-gray-600 text-sm mb-4">
                  Supporting the sporting clubs and community groups that strengthen our region.
                </p>
                <ul className="space-y-2">
                  <li className="flex items-center gap-2 text-gray-700">
                    <span className="w-1.5 h-1.5 bg-yellow-500 rounded-full" />
                    Calder United Lions Football Club
                  </li>
                  <li className="flex items-center gap-2 text-gray-700">
                    <span className="w-1.5 h-1.5 bg-yellow-500 rounded-full" />
                    Calder United Lions Netball Club
                  </li>
                  <li className="flex items-center gap-2 text-gray-700">
                    <span className="w-1.5 h-1.5 bg-yellow-500 rounded-full" />
                    Calder United Lions Hockey Club
                  </li>
                </ul>
              </div>

              {/* Agricultural Development */}
              <div className="bg-white rounded-lg p-6">
                <h3 className="font-bold text-gray-900 mb-3">Agricultural Development</h3>
                <p className="text-gray-600 text-sm">
                  Supporting agricultural innovation and knowledge sharing initiatives that advance farming practices and rural development across our region.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Building Strong Partnerships */}
        <section className="py-16">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Building Strong Partnerships</h2>
            <p className="text-gray-700 mb-6">
              We're proud to be part of the broader agricultural community and maintain strong relationships with industry partners. Our connections help us serve growers better and contribute to the success of Victoria's grain industry.
            </p>
            <a
              href="https://www.nutrienagsolutions.com.au/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-8 py-3 border-2 border-yellow-500 text-yellow-600 font-semibold rounded-md hover:bg-yellow-500 hover:text-gray-900 transition-all duration-300"
            >
              <ExternalLink size={18} />
              Visit Nutrien Ag Solutions
            </a>
          </div>
        </section>

        {/* Sponsorship Enquiries */}
        <section className="py-16 bg-[#1a1a1a]">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-2xl font-bold text-white mb-4">Sponsorship Enquiries</h2>
            <p className="text-gray-300 mb-6">
              Interested in partnering with us or seeking sponsorship for your community initiative? We'd love to hear from you.
            </p>
            <Link
              to="/contact"
              className="inline-block px-8 py-3 bg-yellow-500 text-gray-900 font-semibold rounded-md hover:bg-yellow-400 transition-all duration-300"
            >
              Contact Us
            </Link>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default SponsorshipPage;
